/**
 * Hcell SDK 스크립트 생성기
 *
 * iframe 내부에서 실행될 스크립트들을 생성하는 유틸리티 클래스
 */
export class HcellScriptGenerator {
  static readonly MSG_SCRIPT_EXECUTED = 'SCRIPT_EXECUTED';
  static readonly MSG_IMAGE_INSERT_RESULT = 'IMAGE_INSERT_RESULT';
  static readonly MSG_SDK_READY = 'SDK_READY';

  /**
   * SDK polling: SpreadsheetSDK가 준비될 때까지 대기하고 워크북을 얻는 공통 함수
   * @param callbackName - polling 완료 후 호출할 콜백 함수명
   */
  private static generateSdkPollingScript(callbackName: string): string {
    return `
      function waitForSDKAndGetWorkbook(${callbackName === 'callback' ? 'callback' : callbackName}) {
        console.log('Checking SDK availability...');

        if (typeof window.SpreadsheetSDK === 'undefined' || window.SpreadsheetSDK === null) {
          console.log('SpreadsheetSDK not available, waiting 200ms...');
          setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
          return;
        }

        console.log('SpreadsheetSDK found, getting instance...');

        try {
          let sdkInstance = window.SpreadsheetSDK.GetInstance();
          if (!sdkInstance) {
            setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
            return;
          }

          let application = sdkInstance.Application;
          if (!application) {
            setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
            return;
          }

          let activeWorkbook = application.ActiveWorkbook;
          if (!activeWorkbook) {
            try {
              activeWorkbook = application.Workbooks.Add();
              if (!activeWorkbook) {
                setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
                return;
              }
            } catch (createError) {
              console.error('Error creating workbook:', createError);
              setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
              return;
            }
          }

          let activeSheet = activeWorkbook.ActiveSheet;
          if (!activeSheet) {
            setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
            return;
          }

          console.log('All SDK components ready, executing ${callbackName}...');

          window.sdk = window.SpreadsheetSDK;
          window.sdkInstance = sdkInstance;
          window.application = application;
          window.activeWorkbook = activeWorkbook;
          window.activeSheet = activeSheet;

          ${callbackName}(sdkInstance, application, activeWorkbook, activeSheet);

        } catch (error) {
          console.error('Error in waitForSDKAndGetWorkbook:', error);
          setTimeout(() => waitForSDKAndGetWorkbook(${callbackName}), 200);
        }
      }
    `;
  }

  /**
   * postMessage 헬퍼: 부모 창에 메시지 전송
   */
  private static generatePostMessageScript(type: string, payload: string): string {
    return `
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: '${type}',
          ${payload}
        }, '*');
      }
    `;
  }

  /**
   * launcher refresh 헬퍼: SDK 뷰 갱신
   */
  private static generateRefreshScript(): string {
    return `
      try {
        setTimeout(() => {
          if (typeof window.launcherRef !== 'undefined' && window.launcherRef && window.launcherRef.refresh) {
            console.log('Calling launcher refresh...');
            window.launcherRef.refresh();
          }
        }, 100);
      } catch (refreshError) {
        console.error('SDK refresh error:', refreshError);
      }
    `;
  }

  /**
   * 통합 스크립트 생성 함수
   */
  static buildExecutionScript(
    featureId: string,
    sampleCode: string,
    successMessage: string,
    errorMessage: string,
  ): string {
    return `
      console.log('Starting ${featureId} script...');

      ${this.generateSdkPollingScript('callback')}

      function executeFeature(sdkInstance, application, activeWorkbook, activeSheet) {
        try {
          console.log('All SDK components ready, executing ${featureId}...');

          ${sampleCode}

          ${this.generatePostMessageScript(this.MSG_SCRIPT_EXECUTED, `success: true, message: "${successMessage}"`)}

          ${this.generateRefreshScript()}

        } catch (error) {
          console.error('${errorMessage}', error);

          ${this.generatePostMessageScript(this.MSG_SCRIPT_EXECUTED, 'success: false, error: error.message')}
        }
      }

      waitForSDKAndGetWorkbook(executeFeature);
    `;
  }

  /**
   * 이미지 업로드 스크립트 생성
   */
  static generateImageUploadScript(
    fileName: string,
    arrayBuffer: ArrayBuffer,
    index: number,
    dimensions: { width: number; height: number },
  ): string {
    const bytes = Array.from(new Uint8Array(arrayBuffer)).join(',');

    return `
      console.log('Starting image insertion script for file: ${fileName}');

      ${this.generateSdkPollingScript('callback')}

      function insertImageCallback(sdkInstance, application, activeWorkbook, activeSheet) {
        try {
          window.SpreadsheetSDK.WriteFile("/hnc/File/${fileName}", new Uint8Array([${bytes}]));
          console.log('File uploaded to sandbox: /hnc/File/${fileName}');

          let enums = sdkInstance.Enums;
          let msoTriState = enums.MsoTriState;

          const originalWidth = ${dimensions.width};
          const originalHeight = ${dimensions.height};
          const maxWidth = 300;
          const maxHeight = 200;

          let finalWidth, finalHeight;
          if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
            finalWidth = originalWidth;
            finalHeight = originalHeight;
          } else {
            const widthRatio = maxWidth / originalWidth;
            const heightRatio = maxHeight / originalHeight;
            const ratio = Math.min(widthRatio, heightRatio);
            finalWidth = Math.round(originalWidth * ratio);
            finalHeight = Math.round(originalHeight * ratio);
          }

          activeSheet.Shapes.AddPicture(
            "/hnc/File/${fileName}",
            msoTriState.msoFalse,
            msoTriState.msoTrue,
            ${50 + index * 260}, ${50 + index * 110}, finalWidth, finalHeight
          );

          console.log("Image inserted successfully: ${fileName}");

          ${this.generateRefreshScript()}

          ${this.generatePostMessageScript(this.MSG_IMAGE_INSERT_RESULT, `fileName: '${fileName}', message: 'Image inserted: ${fileName}'`)}

        } catch (error) {
          console.error("Image insertion error:", error);

          ${this.generatePostMessageScript(this.MSG_SCRIPT_EXECUTED, 'success: false, error: error.message')}
        }
      }

      waitForSDKAndGetWorkbook(insertImageCallback);
    `;
  }

  /**
   * SDK Ready Probe 스크립트 생성
   */
  static generateSdkReadyProbe(pollIntervalMs = 500, maxAttempts = 60): string {
    return `
      (function probeSDKReady() {
        var attempts = 0;
        function check() {
          attempts++;
          if (typeof window.SpreadsheetSDK !== 'undefined' && window.SpreadsheetSDK !== null) {
            try {
              var instance = window.SpreadsheetSDK.GetInstance();
              if (instance && instance.Application) {
                ${this.generatePostMessageScript(this.MSG_SDK_READY, "source: 'SdkReadyProbe', timestamp: Date.now()")}
                return;
              }
            } catch(e) {
              // SDK not ready yet
            }
          }
          if (attempts < ${maxAttempts}) {
            setTimeout(check, ${pollIntervalMs});
          }
        }
        check();
      })();
    `;
  }
}
